import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChartBarIcon,
  ChartPieIcon,
  CpuChipIcon,
  ChevronDownIcon,
  ChevronUpIcon,

} from '@heroicons/react/24/outline';

import Plot from "react-plotly.js";

const ResultDisplay = ({ result, moduleName, moduleColor, moduleId }) => {
  const [activeTab, setActiveTab] = useState('predictions');
  // Utilization: hide threshold/CI/probability behind a toggle by default.
  const [showTechnical, setShowTechnical] = useState(false);

console.log(result);

  if (!result || !result.data) {
    return null;
  }

  const {global_images, logits, predictions, shap, shap_images, shap_plotly, cart_confidence } = result.data;

  // Prepare predictions data for charts.
  // NOTE: XGBoost is hidden for now — the utilization view focuses on the CART
  // model and its Wilson confidence interval (uncomment the filter to restore).
  const HIDDEN_MODELS = ['XGBoost'];

  const predictionData = predictions ? Object.entries(predictions)
    .filter(([region]) => !HIDDEN_MODELS.includes(region))
    .map(([region, value]) => ({
      name: region.charAt(0).toUpperCase() + region.slice(1),
      value: value
    })) : [];

  // Utilization (CART): build a binary plain-language verdict + a 0..1 confidence
  // score. Threshold numbers are intentionally kept out of the default view —
  // they live under the technical toggle — and uncertainty is surfaced through
  // the confidence bar rather than as a separate verdict.
  let utilizationView = null;
  if (moduleId === 'utilization' && cart_confidence) {
    const p = Number(cart_confidence.probability);
    const t = Number(cart_confidence.threshold);
    const ci = cart_confidence.wilson_ci;
    const width = cart_confidence.wilson_ci_width;
    const willUtilize = p >= t;

    // Verdict is strictly binary (likely / unlikely). Uncertainty is conveyed by
    // the confidence bar instead: score (0..1) = the share of the 95% Wilson CI
    // that sits on the predicted side of the threshold. It falls toward 0 (red)
    // when the interval straddles the threshold — i.e. when the model is unsure.
    // Falls back to CI tightness, then null, when bounds are missing.
    // Blend two signals so confident cases actually separate instead of all
    // pinning to "full": (a) how clearly the CI sits on the predicted side of
    // the threshold (directional certainty), and (b) how tight the interval is
    // (precision). A clear-but-wide interval reads lower than a clear-and-tight one.
    let score = null;
    if (Array.isArray(ci)) {
      const lo = Number(ci[0]);
      const hi = Number(ci[1]);
      const span = hi - lo;
      const sideFraction = span > 0
        ? (willUtilize ? (hi - t) / span : (t - lo) / span)
        : 1;
      const tightness = 1 - span; // span is on a 0..1 probability scale
      score = Math.max(0, Math.min(1, sideFraction)) * Math.max(0, Math.min(1, tightness));
    } else if (typeof width === 'number') {
      score = 1 - width;
    }
    if (score != null) score = Math.max(0, Math.min(1, score));

    const verdict = willUtilize
      ? 'This organ is likely to be accepted within 100 offers.'
      : 'This organ is unlikely to be accepted within 100 offers.';
    const tone = willUtilize ? 'utilized' : 'not_utilized';

    const confidenceLabel =
      score == null ? 'Unknown'
        : score >= 0.8 ? 'High confidence'
        : score >= 0.5 ? 'Moderate confidence'
        : 'Low confidence';

    // Discretize into 5 blocks (always ≥1 so the meter never looks empty). Chunks
    // avoid implying more precision than the model has.
    const filledChunks = score == null ? 0 : Math.max(1, Math.round(score * 5));

    utilizationView = { p, t, ci, width, score, filledChunks, verdict, tone, confidenceLabel };
  }

  // Colors for pie chart

  // Generate scatter plot data


  // SHAP Chart Component
  const ShapCharts = ({ region, shapData }) => {
    const plot = shapData;
    if (!plot) return null;

    return (
      <div key={region} className="mb-4">
        {/* Interactive Plotly SHAP chart (static image removed) */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <Plot
            data={plot.data}
            layout={{
              ...plot.layout,
              autosize: true,
              margin: { l: 150, r: 40, t: 40, b: 40 }
            }}
            config={{
              responsive: true,
              displaylogo: false
            }}
            style={{ width: "100%", height: "100%" }}
            useResizeHandler={true}
          />
        </div>
      </div>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm"
    >
      <div className="border-b border-gray-200">
        <div className="flex px-6">
          <button
            onClick={() => setActiveTab('predictions')}
            className={`flex items-center space-x-2 px-4 pl-0 py-3 text-sm font-medium transition-all duration-200 border-b-2 ${
              activeTab === 'predictions'
                ? `border-${moduleColor}-500 text-${moduleColor}-600`
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <ChartPieIcon className={`h-4 w-4 ${activeTab === 'predictions' ? `text-${moduleColor}-500` : 'text-gray-400'}`} />
            <span>Results & Chat</span>
          </button>
          {/* SHAP is XGBoost-derived; hidden while the utilization view focuses on CART. */}
          {moduleId !== 'utilization' && (
          <button
            onClick={() => setActiveTab('shap')}
            className={`flex items-center space-x-2 px-4 py-3 text-sm font-medium transition-all duration-200 border-b-2 ${
              activeTab === 'shap'
                ? `border-${moduleColor}-500 text-${moduleColor}-600`
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <CpuChipIcon className={`h-4 w-4 ${activeTab === 'shap' ? `text-${moduleColor}-500` : 'text-gray-400'}`} />
            <span>SHAP Analysis</span>
          </button>
          )}
        </div>
      </div>

      <div className="p-1">
        <AnimatePresence mode="wait">
          {activeTab === 'predictions' && (
            <motion.div
              key="predictions"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
              className="space-y-6"
            >
              {moduleId === 'utilization' ? (
                <>
                  {/* Model explainer — CART focus */}
                  <div className="text-sm text-gray-600 leading-relaxed">
                    This prediction comes from a <span className="font-medium text-gray-800">CART
                    decision-tree model</span> trained on 2016&ndash;2025 UNOS data (excluding the
                    COVID-disrupted years 2020&ndash;2022), estimating whether this kidney will be
                    accepted within 100 offers.
                  </div>

                  {/* Plain-language verdict — binary (likely / unlikely), no threshold shown */}
                  {utilizationView && (
                    <div
                      className={`rounded-lg p-4 border flex items-start gap-3 ${
                        utilizationView.tone === 'utilized'
                          ? 'bg-emerald-50 border-emerald-200'
                          : 'bg-slate-50 border-slate-200'
                      }`}
                    >
                      <span
                        className={`mt-1 h-2.5 w-2.5 rounded-full flex-shrink-0 ${
                          utilizationView.tone === 'utilized' ? 'bg-emerald-500' : 'bg-slate-400'
                        }`}
                      />
                      <div>
                        <p className="text-xs uppercase tracking-wide font-semibold text-gray-500 mb-0.5">
                          Prediction
                        </p>
                        <p
                          className={`text-base font-semibold ${
                            utilizationView.tone === 'utilized' ? 'text-emerald-900' : 'text-slate-900'
                          }`}
                        >
                          {utilizationView.verdict}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Wilson-CI confidence bar (red → green) */}
                  {utilizationView && utilizationView.score != null && (
                    <div className="bg-white rounded-lg p-4 border border-gray-200">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm font-semibold text-gray-900">Model confidence</h4>
                        <span className="text-sm font-medium text-gray-700">
                          {utilizationView.confidenceLabel}
                        </span>
                      </div>
                      <div className="flex gap-1.5">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <div
                            key={i}
                            className="h-3 flex-1 rounded-sm transition-colors duration-500"
                            style={{
                              backgroundColor: i < utilizationView.filledChunks
                                ? `hsl(${utilizationView.score * 120}, 70%, 45%)`
                                : '#e5e7eb',
                            }}
                          />
                        ))}
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        More filled, greener blocks mean the model is more confident in this
                        prediction; fewer, redder blocks mean it is uncertain.
                      </p>
                    </div>
                  )}

                  {/* Technical details toggle — threshold, probability, CI, etc. */}
                  {utilizationView && (
                    <div>
                      <button
                        type="button"
                        onClick={() => setShowTechnical((v) => !v)}
                        className="flex items-center gap-1 text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors"
                      >
                        {showTechnical ? (
                          <ChevronUpIcon className="h-4 w-4" />
                        ) : (
                          <ChevronDownIcon className="h-4 w-4" />
                        )}
                        {showTechnical ? 'Hide technical details' : 'Show technical details'}
                      </button>

                      {showTechnical && (
                        <div className="mt-3 bg-gray-50 rounded-lg p-4 border border-gray-200 space-y-2 text-sm">
                          <div className="flex justify-between gap-4">
                            <span className="text-gray-500">Calibrated probability of acceptance within 100 offers</span>
                            <span className="font-medium text-gray-900">{utilizationView.p.toFixed(5)}</span>
                          </div>
                          <div className="flex justify-between gap-4">
                            <span className="text-gray-500">Decision threshold</span>
                            <span className="font-medium text-gray-900">{utilizationView.t.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between gap-4">
                            <span className="text-gray-500">Prediction vs. threshold</span>
                            <span className="font-medium text-gray-900">
                              {utilizationView.p >= utilizationView.t ? 'accepted within 100 offers' : 'not accepted within 100 offers'}
                            </span>
                          </div>
                          {Array.isArray(utilizationView.ci) && (
                            <div className="flex justify-between gap-4">
                              <span className="text-gray-500">95% Wilson confidence interval</span>
                              <span className="font-medium text-gray-900">
                                [{utilizationView.ci[0]}, {utilizationView.ci[1]}]
                                {utilizationView.width != null ? ` (width ${utilizationView.width})` : ''}
                              </span>
                            </div>
                          )}
                          {cart_confidence && cart_confidence.confidence_label && (
                            <div className="flex justify-between gap-4">
                              <span className="text-gray-500">Confidence label</span>
                              <span className="font-medium text-gray-900">{cart_confidence.confidence_label}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </>
              ) : (
                /* Acceptance (non-utilization) — unchanged prediction scores box */
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                  <h4 className="text-sm font-semibold text-blue-900 mb-2">
                    {`${moduleName} Prediction Scores`}
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {predictionData.map((region) => (
                      <div key={region.name}>
                        <p className="text-xs text-blue-700">{region.name}</p>
                        <p className="text-lg font-bold text-blue-900">{Number(region.value).toFixed(5)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          )}
          {activeTab === "shap" && (
            <motion.div
              key="shap"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
              className="space-y-6"
            > 
              {Object.keys(shap).map((region) =>{
                return (
                  <div key={region} className="mb-4">
                    <div className="flex items-center justify-between mb-2 p-2">
                      <h4 className="text-sm font-semibold text-gray-900">
                        {region.charAt(0).toUpperCase() + region.slice(1)} Baseline
                      </h4>
                    </div>
                    <ShapCharts
                      region={region}
                      shapData={shap_plotly[region]}
                    />
                  </div>
                );
              })}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

export default ResultDisplay;